import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api.tsx";
import { useError } from "../../contexts/ErrorContext.tsx";
import ConfirmModal from "../../components/ConfirmModal.tsx";
import User from "../../Entities/User.tsx";

const emptyUser = {
    id: "",
    fullName: "",
    userType: "",
    address: "",
    email: "",
    emailConfirmed: false
};

const UserManagementPage = () => {
    const { setError } = useError();
    const [users, setUsers] = useState<User[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editUser, setEditUser] = useState(emptyUser);
    const [confirmModal, setConfirmModal] = useState<{ show: boolean; userId?: string }>({ show: false });

    const fetchUsers = useCallback(async () => {
        try {
            const token = localStorage.getItem("access_token");
            const res = await api.get("/api/user", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(res.data);
        } catch (err) {
            setError(err.response?.data?.Message || "Bir hata oluştu.");
        }
    }, [setError]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // EDIT
    const startEdit = (user: any) => {
        setEditingId(user.id);
        setEditUser({
            id: user.id,
            fullName: user.fullName,
            userType: user.userType,
            address: user.address,
            email: user.email,
            emailConfirmed: user.emailConfirmed
        });
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, type, value } = e.target;
        const newValue =
            type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : value;
        setEditUser({ ...editUser, [name]: newValue });
    };

    const handleEditSave = async (id: string) => {
        try {
            const token = localStorage.getItem("access_token");
            await api.put(`/api/user`, editUser, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEditingId(null);
            fetchUsers();
        } catch (err) {
            alert("Kullanıcı güncellenemedi.");
            setError(err.response?.data?.Message || "Bir hata oluştu.");
        }
    };

    // DELETE
    const handleDelete = async (id: string) => {
        setConfirmModal({ show: true, userId: id });
    };

    const confirmDelete = async () => {
        if (!confirmModal.userId) return;
        try {
            const token = localStorage.getItem("access_token");
            await api.delete(`/api/user/${confirmModal.userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchUsers();
        } catch (err) {
            alert("Kullanıcı silinemedi.");
            setError(err.response?.data?.Message || "Bir hata oluştu.");
        } finally {
            setConfirmModal({ show: false });
        }
    };

    return (
        <div className="p-8">
            <h1 className="text-xl font-bold mb-3">Kullanıcı Yönetimi</h1>
            <p className="mb-6 text-sm text-gray-600">Kayıtlı üye bilgileri</p>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border rounded text-sm">
                    <thead>
                        <tr className="bg-gray-100 text-left">
                            <th className="py-1 px-2 border-b">Ad Soyad</th>
                            <th className="py-1 px-2 border-b">Kullanıcı Tipi</th>
                            <th className="py-1 px-2 border-b">Adres</th>
                            <th className="py-1 px-2 border-b">Email</th>
                            <th className="py-1 px-2 border-b">Email Onay</th>
                            <th className="py-1 px-2 border-b">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                {editingId === user.id ? (
                                    <>
                                        <td className="py-1 px-2 border-b">
                                            <input
                                                type="text"
                                                name="fullName"
                                                value={editUser.fullName}
                                                onChange={handleEditChange}
                                                className="border rounded px-2 py-1 w-full"
                                            />
                                        </td>
                                        <td className="py-1 px-2 border-b">
                                            <select
                                                name="customerType"
                                                value={editUser.userType || ""}
                                                onChange={handleEditChange}
                                                className="form-select border rounded p-1"
                                            >
                                                <option value="">Seçiniz</option>
                                                <option value="bireysel">Bireysel</option>
                                                <option value="kurumsal">Kurumsal</option>
                                            </select>
                                        </td>
                                        <td className="py-1 px-2 border-b">
                                            <input
                                                type="text"
                                                name="address"
                                                value={editUser.address}
                                                onChange={handleEditChange}
                                                className="border rounded px-2 py-1 w-full"
                                            />
                                        </td>
                                        <td className="py-1 px-2 border-b">{editUser.email}</td>
                                        <td className="py-1 px-2 border-b">
                                            <input
                                                type="checkbox"
                                                name="emailConfirmed"
                                                checked={!!editUser.emailConfirmed}
                                                onChange={handleEditChange}
                                                className="form-checkbox h-5 w-5 text-green-600"
                                            />
                                        </td>
                                        <td className="py-1 px-2 border-b">
                                            <button
                                                className="text-green-600 hover:underline mr-2"
                                                onClick={() => handleEditSave(user.id)}
                                            >
                                                Kaydet
                                            </button>
                                            <button
                                                className="text-gray-600 hover:underline"
                                                onClick={() => setEditingId(null)}
                                            >
                                                İptal
                                            </button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td className="py-1 px-2 border-b">{user.fullName}</td>
                                        <td className="py-1 px-2 border-b">{user.userType}</td>
                                        <td className="py-1 px-2 border-b">{user.address}</td>
                                        <td className="py-1 px-2 border-b">{user.email}</td>
                                        <td className="py-1 px-2 border-b">
                                            {user.emailConfirmed ? (
                                                <span className="text-green-600 font-semibold">Onaylı</span>
                                            ) : (
                                                <span className="text-red-600 font-semibold">Onaysız</span>
                                            )}
                                        </td>
                                        <td className="py-1 px-2 border-b">
                                            <button
                                                className="text-blue-600 hover:underline mr-2"
                                                onClick={() => startEdit(user)}
                                            >
                                                Düzenle
                                            </button>
                                            <button
                                                className="text-red-600 hover:underline"
                                                onClick={() => handleDelete(user.id)}
                                            >
                                                Sil
                                            </button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan={5} className="py-4 text-center text-gray-400">
                                    Kayıtlı kullanıcı bulunamadı.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <ConfirmModal
                    show={confirmModal.show}
                    title="Kullanıcı Sil"
                    message="Bu kullanıcıuı silmek istediğinize emin misiniz?"
                    onConfirm={confirmDelete}
                    onCancel={() => setConfirmModal({ show: false })}
                    confirmText="Sil"
                    cancelText="Vazgeç"
                />
            </div>
        </div>
    );
};

export default UserManagementPage;  